import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Ic } from './icons';

/* ─────────────────────────────────────────────────────────────
   ERPForce PDF Builder — field picker
   A friendly dropdown of the document's available data fields, so
   admins choose fields from a list (no typing, no spelling mistakes).
   Exposes export const PBPicker = { FieldPicker, buildFieldCatalog, fieldLabel, humanize }
   ───────────────────────────────────────────────────────────── */

  const { useState, useRef, useEffect } = React;
  

  // turn a raw path into readable words: "doc.dueDate" → "Due date"
  function humanize(path) {
    let s = String(path || '').replace(/^this\./, '').replace(/^@/, '').replace(/\./g, ' ');
    s = s.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/\s+/g, ' ').trim().toLowerCase();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Field';
  }

  // build a catalog from a document's field groups
  function buildFieldCatalog(groups) {
    const list = [], byExpr = {};
    (groups || []).forEach(g => (g.fields || []).forEach(fl => {
      if (fl.loop) return;                                   // skip structural loops
      const inner = fl.tag.replace(/^\{\{\{?/, '').replace(/\}?\}\}$/, '').trim();
      list.push({ group: g.label, label: fl.label, tag: fl.tag, expr: inner });
      if (!(inner in byExpr)) byExpr[inner] = fl.label;
      const path = inner.split(/\s+/).pop();
      if (!(path in byExpr)) byExpr[path] = fl.label;        // path-only fallback
    }));
    return { list, byExpr };
  }

  // friendly label for a field, given its helper + path
  function fieldLabel(catalog, helper, path) {
    const inner = helper ? helper + ' ' + path : path;
    return (catalog && (catalog.byExpr[inner] || catalog.byExpr[path])) || humanize(path);
  }

  function FieldPicker({ catalog, rect, current, onPick, onClose }) {
    const [q, setQ] = useState('');
    const ref = useRef(null);

    useEffect(() => {
      const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
      const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
      document.addEventListener('mousedown', onDoc, true);
      document.addEventListener('keydown', onKey, true);
      return () => { document.removeEventListener('mousedown', onDoc, true); document.removeEventListener('keydown', onKey, true); };
    }, [onClose]);

    const W = 288, MAXH = 380;
    let left = Math.min(rect.left, window.innerWidth - W - 12);
    left = Math.max(12, left);
    let top = rect.bottom + 6;
    if (top + MAXH > window.innerHeight - 12) top = Math.max(12, rect.top - MAXH - 6);

    const ql = q.trim().toLowerCase();
    const grouped = [];
    const idx = {};
    catalog.list.forEach(it => {
      if (ql && !(it.label.toLowerCase().includes(ql) || it.group.toLowerCase().includes(ql) || it.expr.toLowerCase().includes(ql))) return;
      if (!(it.group in idx)) { idx[it.group] = grouped.length; grouped.push({ group: it.group, items: [] }); }
      grouped[idx[it.group]].items.push(it);
    });

    return (
      <div className="pb-fp" ref={ref} style={{ left, top, width: W, maxHeight: MAXH }}>
        <div className="pb-fp-head">
          <Ic k="search" />
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Find a field…" />
        </div>
        <div className="pb-fp-list">
          {grouped.length === 0 && <div className="pb-fp-empty">No matching fields</div>}
          {grouped.map(({ group, items }) => (
            <div key={group} className="pb-fp-grp">
              <div className="pb-fp-grp-h">{group}</div>
              {items.map((it, i) => (
                <button key={i} className={`pb-fp-item ${it.expr === current ? 'is-current' : ''}`} onClick={() => onPick(it.tag)}>
                  <span className="pb-fp-item-l">{it.label}</span>
                  {it.expr === current && <Ic k="check" />}
                </button>
              ))}
            </div>
          ))}
        </div>
        <div className="pb-fp-foot">Filled in automatically with real data when the document is sent.</div>
      </div>
    );
  }

  export const PBPicker = { FieldPicker, buildFieldCatalog, fieldLabel, humanize };

